/**
 * @file userService.js
 * @description APIs for User related requests.
 **/

import { systemHttp } from './httpService';
import qs from 'qs';

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Fetches plan types.
 * @returns {Promise<Array>} - A promise that resolves to an array of plan types.
 * @throws {Error} - If there is an error fetching the plan types.
 */
export const queryPlanTypes = async payload => {
    try {
        const response = await systemHttp.get(`/v2/system/plan-type`);
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 * Fetches employment statuses.
 * @returns {Promise<Array>} - A promise that resolves to an array of employment statuses.
 * @throws {Error} - If there is an error fetching the employment statuses.
 */
export const fetchEmploymentStatuses = async () => {
    try {
        const response = await systemHttp.get('/v2/system/employment-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching employment statuses:', error);
        throw error;
    }
};

/**
 * Fetches pay frequencies.
 * @returns {Promise<Array>} - A promise that resolves to an array of pay frequencies.
 * @throws {Error} - If there is an error fetching the pay frequencies.
 */
export const fetchPayFrequencies = async () => {
    try {
        const response = await systemHttp.get('/v2/system/pay-frequency');
        return response.data;
    } catch (error) {
        console.error('Error fetching pay frequencies:', error);
        throw error;
    }
};

/**
 * Fetches tiers.
 * @returns {Promise<Array>} - A promise that resolves to an array of tiers.
 * @throws {Error} - If there is an error fetching the tiers.
 */
export const fetchTiers = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tier');
        return response.data;
    } catch (error) {
        console.error('Error fetching tiers:', error);
        throw error;
    }
};

/**
 * Retrieves a list of all agencies.
 *
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the agencies.
 */
export const getAgencies = async (
    page = 1,
    pageSize = 2147483647,
    sort = null,
    order = null,
    reseller = null,
) => {
    try {
        const searchCriteria = {
            page,
            pageSize,
            sort,
            order,
            isReseller: reseller,
        };
        const queryString = qs.stringify(searchCriteria, {
            arrayFormat: 'repeat',
        });
        const response = await systemHttp.get(
            `/v2/system/agency?${queryString}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve agencies');
    }
};
export const fetchAgencies = getAgencies;

/**
 * Retrieves a single agency by ID.
 *
 * @param {number} [agencyId] - The ID of the agency.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the agency.
 */
export const getAgency = async agencyId => {
    try {
        const response = await systemHttp.get(`/v2/system/agency/${agencyId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve agency');
    }
};
export const fetchAgency = getAgency;

/**
 * Creates a new agency record.
 *
 * @param {Object} data - The data for the new agency.
 * @returns {Promise<Object>} - The response data with the created agency record.
 * @throws {Error} - If there is an error creating the agency record.
 */
export const createAgency = async data => {
    try {
        const response = await systemHttp.post('/v2/system/agency', {
            ...data,
        });
        return response.data;
    } catch (error) {
        console.error('Error creating agency:', error);
        throw error;
    }
};

/**
 * Updates an agency.
 *
 * @param {Object} agencyData - The agency data to be updated.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the agency.
 */
export const updateAgency = async agencyData => {
    try {
        const response = await systemHttp.put(`/v2/system/agency`, agencyData);
        return response.data;
    } catch (error) {
        console.error('Failed to update agency:', error);
        throw error;
    }
};

/**
 * Delete multiple agencies.
 * @param {Array<number>} agencyIds - The IDs of the agencies to delete.
 * @returns {Promise<void>}
 */
export const deleteAgencies = async agencyIds => {
    try {
        return Promise.all(
            agencyIds.map(agencyId => {
                return deleteAgency(agencyId);
            }),
        );
    } catch (error) {
        console.error('Error deleting agencies:', error);
        throw error;
    }
};

export const deleteAgency = async agencyId => {
    try {
        const response = await systemHttp.delete(
            `/v2/system/agency/${agencyId}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error deleting agency:', error);
        throw error;
    }
};

/**
 * Fetches office details for a specific agency.
 * @param {string} agencyId - The ID of the agency.
 * @returns {Promise<Array>} - A promise that resolves to an array of office details.
 * @throws {Error} - If there is an error fetching the office details.
 */
export const fetchAgencyOffices = async agencyId => {
    try {
        const response = await systemHttp.get(
            `/v2/system/agency/${agencyId}/office`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching agency locations:', error);
        throw error;
    }
};

/**
 * Creates a new agency office record.
 *
 * @param {Object} data - The data for the new agency office.
 * @returns {Promise<Object>} - The response data with the created agency office record.
 * @throws {Error} - If there is an error creating the agency office record.
 */
export const createAgencyOffice = async data => {
    try {
        const response = await systemHttp.post('/v2/system/agency/office', {
            ...data,
        });
        return response.data;
    } catch (error) {
        console.error('Error creating agency office:', error);
        throw error;
    }
};

/**
 * Updates an agency office.
 *
 * @param {Object} agencyOfficeData - The agency data to be updated.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the agency.
 */
export const updateAgencyOffice = async agencyOfficeData => {
    try {
        const response = await systemHttp.put(
            `/v2/system/agency/office`,
            agencyOfficeData,
        );
        return response.data;
    } catch (error) {
        console.error('Failed to update agency:', error);
        throw error;
    }
};

export const deleteAgencyOffice = async agencyOfficeId => {
    try {
        const response = await systemHttp.delete(
            `/v2/system/agency/${agencyOfficeId}/office`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error deleting agency office:', error);
        throw error;
    }
};

export const fetchSICCodes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/sic-code');
        return response.data;
    } catch (error) {
        console.error('Error fetching sic codes:', error);
        throw error;
    }
};

/**
 * Fetches application timeout details.
 * @returns {Promise<Array>} - A promise that resolves to an array of application timeout details.
 * @throws {Error} - If there is an error fetching the application timeout details.
 */
export const fetchApplicationTimeouts = async () => {
    try {
        const response = await systemHttp.get('/v2/system/application-timeout');
        return response.data;
    } catch (error) {
        console.error('Error fetching application timeouts:', error);
        throw error;
    }
};

export const fetchTenantTiers = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-tier');
        return response.data;
    } catch (error) {
        console.error('Error fetching tiers:', error);
        throw error;
    }
};

/**
 * Fetches contact types.
 * @returns {Promise<Array>} - A promise that resolves to an array of contact types.
 * @throws {Error} - If there is an error fetching the contact types.
 */
export const fetchContactType = async () => {
    try {
        const response = await systemHttp.get('/v2/system/contact-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching contact types:', error);
        throw error;
    }
};

export const fetchClientTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching client types:', error);
        throw error;
    }
};

/**
 * Fetches population ranges.
 * @returns {Promise<Array>} - A promise that resolves to an array of population ranges.
 * @throws {Error} - If there is an error fetching the population ranges.
 */
export const fetchPopulationRanges = async () => {
    try {
        const response = await systemHttp.get('/v2/system/population-range');
        return response.data;
    } catch (error) {
        console.error('Error fetching population ranges:', error);
        throw error;
    }
};

/**
 * Fetches the list of Sic Codes.
 * @returns {Promise<Array>} - A promise that resolves to an array of Sic Codes.
 * @throws {Error} - If there is an error fetching the Sic Codes.
 */
export const fetchSicCodes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/sic-code');
        return response.data;
    } catch (error) {
        console.error('Error fetching Sic Codes:', error);
        throw error;
    }
};

export const fetchLogoutTimeouts = async () => {
    try {
        const response = await systemHttp.get('/v2/system/application-timeout');
        return response.data;
    } catch (error) {
        console.error('Error fetching logout timeouts:', error);
        throw error;
    }
};

/**
 * Fetches client tiers.
 * @returns {Promise<Array>} - A promise that resolves to an array of client tiers.
 * @throws {Error} - If there is an error fetching the client tiers.
 */
export const fetchClientTiers = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-tier');
        return response.data;
    } catch (error) {
        console.error('Error fetching client tiers:', error);
        throw error;
    }
};

/**
 * Fetches client status.
 * @returns {Promise<Array>} - A promise that resolves to an array of client status.
 * @throws {Error} - If there is an error fetching the client status.
 */
export const fetchClientStatus = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching client status:', error);
        throw error;
    }
};

export const fetchContactTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/contact-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching logout timeouts:', error);
        throw error;
    }
};

/**
 * Fetches favorited clients for specific user.
 * @returns {Promise<Array>} - A promise that resolves to an array of clients.
 * @throws {Error} - If there is an error fetching the clients.
 */

export const fetchFavoriteClients = async () => {
    try {
        const response = await systemHttp.get('/v2/system/user/favorites');
        return response.data;
    } catch (error) {
        console.error('Error fetching favorite clients:', error);
        throw error;
    }
};

/**
 * Fetches admin users matching search criteria, if any specified
 *
 * @param {Object} searchCriteria - The object containing the search criteria.
 * @returns {Promise<Array>} - The response data with search results.
 * @throws {Error} - If there is an error performing the search.
 */
export const fetchAdminUsers = async searchCriteria => {
    try {
        const queryString = qs.stringify(
            { ...searchCriteria },
            { arrayFormat: 'repeat' },
        );

        const response = await systemHttp.get(
            `/v2/system/admin-user/search?${queryString}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching admin users:', error);
        throw error;
    }
};

/**
 * Fetches single admin user.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} - A promise that resolves to a single admin user.
 * @throws {Error} - If there is an error fetching the single admin user.
 */
export const fetchAdminUserById = async userId => {
    try {
        const response = await systemHttp.get(`v2/system/admin-user/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching single admin user:', error);
        throw error;
    }
};

/**
 * Creates a single admin user
 *
 * @param {Object} clientData - The admin user data to be added.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the user.
 */
export const createAdminUser = async userData => {
    try {
        const response = await systemHttp.post(
            `v2/system/admin-user`,
            userData,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating admin user:', error);
        throw error;
    }
};

/**
 * Updates a single admin user
 *
 * @param {Object} clientData - The admin user data to be updated.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the user.
 */
export const updateAdminUser = async userData => {
    try {
        const response = await systemHttp.put(`v2/system/admin-user`, userData);
        return response.data;
    } catch (error) {
        console.error('Error updating admin user:', error);
        throw error;
    }
};

/**
 * Deletes a single admin user.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} - A promise that resolves to a delete.
 * @throws {Error} - If there is an error deleting the single admin user.
 */
export const deleteAdminUserById = async userId => {
    try {
        const response = await systemHttp.delete(
            `v2/system/admin-user/${userId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting single admin user:', error);
        throw error;
    }
};

/**
 * Fetches employment categories.
 * @returns {Promise<Array>} - A promise that resolves to an array of employment categories.
 * @throws {Error} - If there is an error fetching the employment categories.
 */
export const fetchEmploymentCategories = async () => {
    try {
        const response = await systemHttp.get('/v2/system/employment-category');
        return response.data;
    } catch (error) {
        console.error('Error fetching employment categories:', error);
        throw error;
    }
};

/**
 * Fetches vendors.
 * @returns {Promise<Array>} - A promise that resolves to an array of vendors.
 * @throws {Error} - If there is an error fetching the vendors.
 */
export const fetchVendors = async () => {
    try {
        const response = await systemHttp.get('/v2/system/vendor');
        return response.data;
    } catch (error) {
        console.error('Error fetching vendors:', error);
        throw error;
    }
};

/**
 * Fetches a single Vendor by ID.
 * @param {string} vendorId - The ID of the Vendor.
 * @returns {Promise<Object>} - A promise that resolves to fetch a single Vendor.
 * @throws {Error} - If there is an error fetching the single Vendor.
 */
export const fetchVendorById = async vendorId => {
    try {
        const response = await systemHttp.get(`v2/system/vendor/${vendorId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching vendor by id:', error);
        throw error;
    }
};

/**
 * Deletes a single vendor by ID.
 * @param {string} vendorId - The ID of the vendor.
 * @returns {Promise<Object>} - A promise that resolves to delete a single vendor.
 * @throws {Error} - If there is an error deleting the single Vendor.
 */
export const deleteVendorById = async vendorId => {
    try {
        const response = await systemHttp.delete(
            `v2/system/vendor/${vendorId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting vendor by id:', error);
        throw error;
    }
};

/**
 * Creates a new vendor record.
 * @param {Object} vendorData - The data for the new vendor.
 * @returns {Promise<Object>} -  A promise that resolves to create the vendor record.
 * @throws {Error} - If there is an error creating the vendor record.
 */
export const createVendor = async vendorData => {
    try {
        const response = await systemHttp.post('/v2/system/vendor', vendorData);
        return response.data;
    } catch (error) {
        console.error('Error creating vendor:', error);
        throw error;
    }
};

/**
 * Updates an existing vendor record.
 * @param {Object} vendorData - The vendor data to be updated.
 * @returns {Promise<Object>} -  A promise that resolves to update the vendor.
 * @throws {Error} - If there is an error updating the vendor
 */
export const updateVendor = async vendorData => {
    try {
        const response = await systemHttp.put('/v2/system/vendor', vendorData);
        return response.data;
    } catch (error) {
        console.error('Error updating vendor:', error);
        throw error;
    }
};

/**
 * Fetches network types.
 * @returns {Promise<Array>} - A promise that resolves to an array of network types.
 * @throws {Error} - If there is an error fetching the network types.
 */
export const fetchNetworkTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/network-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching network types:', error);
        throw error;
    }
};

/**
 * Fetches locales (languages).
 * @returns {Promise<Array>} - A promise that resolves to an array of locales.
 * @throws {Error} - If there is an error fetching the locales.
 */
export const fetchLocales = async () => {
    try {
        const response = await systemHttp.get('/v2/system/locale');
        return response.data;
    } catch (error) {
        console.error('Error fetching locales:', error);
        throw error;
    }
};

/**
 * Fetches tenant user statues (account status).
 * @returns {Promise<Array>} - A promise that resolves to an array of tenant user statuses.
 * @throws {Error} - If there is an error fetching the statuses
 */
export const fetchTenantUserStatuses = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-user-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching tenant user statuses:', error);
        throw error;
    }
};

/**
 * Fetches contact roles.
 * @returns {Promise<Array>} - A promise that resolves to an array of contact roles.
 * @throws {Error} - If there is an error fetching the contact roles.
 */
export const fetchContactRoles = async () => {
    try {
        const response = await systemHttp.get('/v2/system/contact-role');
        return response.data;
    } catch (error) {
        console.error('Error fetching contact roles:', error);
        throw error;
    }
};

/**
 * Fetches billing types (Current/Arrears) used on the Billing Details tab.
 * @returns {Promise<Array>} - A promise that resolves to an array of billing types.
 * @throws {Error} - If there is an error fetching the billing types.
 */
export const fetchBillingTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/bill-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching billing types:', error);
        throw error;
    }
};

/**
 * Fetches billing statuses used on the Billing Clients list.
 * @returns {Promise<Array>} - A promise that resolves to an array of billing statuses.
 * @throws {Error} - If there is an error fetching the billing statuses.
 */
export const fetchBillStatuses = async () => {
    try {
        const response = await systemHttp.get('/v2/system/bill-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching billing statuses:', error);
        throw error;
    }
};

/**
 * Fetches billable features used on the Billing Services tab.
 * @returns {Promise<Array>} - A promise that resolves to an array of bill features.
 * @throws {Error} - If there is an error fetching the bill features.
 */
export const fetchBillFeatures = async () => {
    try {
        const response = await systemHttp.get('/v2/system/bill-feature');
        return response.data;
    } catch (error) {
        console.error('Error fetching bill features:', error);
        throw error;
    }
};

/**
 * Fetches bill cost types (PEPM/Fixed/Percentage) used on the Billing Services tab.
 * @returns {Promise<Array>} - A promise that resolves to an array of bill cost types.
 * @throws {Error} - If there is an error fetching the bill cost types.
 */
export const fetchBillCostTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/bill-cost-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching bill cost types:', error);
        throw error;
    }
};

/**
 * Fetches bill cost frequencies (Monthly/One Time/Quarterly/Annually) used on the Billing Services tab.
 * @returns {Promise<Array>} - A promise that resolves to an array of bill cost frequencies.
 * @throws {Error} - If there is an error fetching the bill cost frequencies.
 */
export const fetchBillCostFrequencies = async () => {
    try {
        const response = await systemHttp.get('/v2/system/bill-cost-frequency');
        return response.data;
    } catch (error) {
        console.error('Error fetching bill cost frequencies:', error);
        throw error;
    }
};

/**
 * Fetches contract statuses (Negotiation/Client Signature/Internal Signature/Completed) used on the Contracts tab.
 * @returns {Promise<Array>} - A promise that resolves to an array of contract statuses.
 * @throws {Error} - If there is an error fetching the contract statuses.
 */
export const fetchContractStatuses = async () => {
    try {
        const response = await systemHttp.get('/v2/system/contract-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching contract statuses:', error);
        throw error;
    }
};

/**
 * Fetches Plan Details.
 * @param {number} page - The page number for pagination.
 * @param {number} pageSize - The number of items per page.
 * @param {string} search - The search query.
 * @param {string} sortName - The field name to sort by.
 * @param {string} sortOrder - The order of sorting (asc/desc).
 * @returns {Promise<Object>} - A promise that resolves to an array of Plan Details objects.
 * @throws {Error} - If there is an error fetching the Plan Details.
 */
export const fetchPlanDetails = async (
    page,
    pageSize,
    search,
    sortName,
    sortOrder,
) => {
    try {
        const response = await systemHttp.get(
            `/v2/system/system-plan-detail?page=${page}&pageSize=${pageSize}&search=${search}&sort=${sortName}&order=${sortOrder}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching plan details:', error);
        throw error;
    }
};

/**
 * Fetches plan types for when the user is editing or creating Plan Details.
 * @returns {Promise<Array>} - A promise that resolves to an array of plan types.
 * @throws {Error} - If there is an error fetching the plan types.
 */
export const fetchPlanDetailPlanTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/plan-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching plan types:', error);
        throw error;
    }
};

/**
 * Fetches a single Plan Detail by ID.
 * @param {string} planDetailId - The ID of the Plan Detail.
 * @returns {Promise<Object>} - A promise that resolves to fetch a single Plan Detail.
 * @throws {Error} - If there is an error fetching the single Plan Detail.
 */
export const fetchPlanDetailById = async planDetailId => {
    try {
        const response = await systemHttp.get(
            `/v2/system/system-plan-detail/${planDetailId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching plan detail by id:', error);
        throw error;
    }
};

/**
 * Deletes a single Plan Details by ID.
 * @param {string} planDetailsId - The ID of the Plan Details object.
 * @returns {Promise<Object>} - A promise that resolves to delete a Plan Details object.
 * @throws {Error} - If there is an error deleting the single Plan Details object.
 */
export const deletePlanDetailById = async planDetailsId => {
    try {
        const response = await systemHttp.delete(
            `/v2/system/system-plan-detail/${planDetailsId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting plan details by id:', error);
        throw error;
    }
};

/**
 * Creates a new Plan Details record.
 * @param {Object} planDetailData - The data for the new Plan Details.
 * @returns {Promise<Object>} -  A promise that resolves to create the Plan Details record.
 * @throws {Error} - If there is an error creating the Plan Details record.
 */
export const createPlanDetail = async planDetailData => {
    try {
        const response = await systemHttp.post(
            '/v2/system/system-plan-detail',
            planDetailData,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating plan detail:', error);
        throw error;
    }
};

/**
 * Updates an existing Plan Details record.
 * @param {Object} planDetailData - The Plan Details data to be updated.
 * @returns {Promise<Object>} -  A promise that resolves to update the Plan Details.
 * @throws {Error} - If there is an error updating the Plan Details
 */
export const updatePlanDetail = async planDetailData => {
    try {
        const response = await systemHttp.put(
            '/v2/system/system-plan-detail',
            planDetailData,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating plan detail:', error);
        throw error;
    }
};

/**
 * Fetches notification tokens.
 * @returns {Promise<Object>} - A promise that resolves to an array of notification tokens.
 * @throws {Error} - If there is an error fetching the notification tokens.
 */
export const fetchNotificationTokens = async () => {
    try {
        const response = await systemHttp.get(`/v2/system/notification-token`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notification tokens:', error);
        throw error;
    }
};

/**
 * Fetches genders.
 * @returns {Promise<Array>} - A promise that resolves to an array of genders.
 * @throws {Error} - If there is an error fetching the genders.
 */
export const fetchGenders = async () => {
    try {
        const response = await systemHttp.get('/v2/system/gender');
        return response.data;
    } catch (error) {
        console.error('Error fetching genders:', error);
        throw error;
    }
};
